import toastMessage from "../../toastMessage";
import { getStorage } from "../../storage";
import language from "../../../language";
import {
  defineRoleCenterSite,
  hasNationalInventory,
} from "../../handleAccessRoles";
import moment from "moment";
import api from "../../api";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

export const fetchInventoryData = async (
  lng = "english",
  {
    page,
    limit,
    donation,
    donor,
    id,
    type,
    center,
    centerSite,
    bloodGroup,
    q,
    isCount,
    status,
    donationNumber,
    sortField,
    startDate,
    endDate,
    receivedBy,
    startReceivedDate,
    endReceivedDate,
    code,
  },
) => {
  try {
    const user = await getStorage();

    const { centerRole, centerSiteRole } = await defineRoleCenterSite();

    let query = {
      page: page,
      limit: limit,
      donation,
      donor,
      id,
      center: centerRole || center,
      centerSite: centerSiteRole || centerSite,
      bloodGroup,
      q,
      count: isCount,
      status,
      donationNumber,
      sortField,
      startReceivedDate: startDate,
      endReceivedDate: endDate,
      receivedBy,
      code,
    };

    if (query.count) {
      delete query.page;
      delete query.limit;
    }

    const options = {
      method: "GET",
      url: `${API_URL}/bloodInventory`,
      params: query,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    if (type === "dropdown") {
      return queryRes.data.map((el) => {
        return {
          ...el,
          label: `${el.category},  ${el.bloodGroup}, ${el.donation._id}, ${el.label}`,
          value: el._id,
        };
      });
    }

    let { data, count, totalRecords } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    return { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};

export const deleteInventory = async (lng = "english", id) => {
  try {
    const user = await getStorage();

    const options = {
      method: "DELETE",
      url: `${API_URL}/bloodInventory`,
      data: {
        id,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    await api(options);

    toastMessage("success", language[lng].delete_data_success);
  } catch (error) {
    toastMessage("error", error);
  }
};

export const fetchInventories = async (
  lng = "english",
  {
    page,
    limit,
    donation,
    donationNumber,
    id,
    code,
    center,
    centerSite,
    status,
    startDate,
    endDate,
    isCount,
    isLabeled,
    receivedBy,
    reason,
    component,
    bloodGroup,
    productIds,
    productType,
    countStatusParams = {},
    inventoryStatus,
    sourceStock,
    exactStock,
    excludeIncomingReception,
    excludeRejectedPending,
    awaitingReceptionFor,
    onlyNotExpired,
    onlyExpired,
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
    startExpiredDate,
    endExpiredDate,
    startReturnDate,
    endReturnDate,
    allowStatusWithDonation,
    noStockFilter,
    q,
  },
) => {
  try {
    const user = await getStorage();

// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
    const { centerRole, centerSiteRole, stock } = await defineRoleCenterSite();

    let filters = {
      page: page,
      limit: limit,
      donation,
      donationNumber,
      id,
      // center: centerRole || center,
      // centerSite: centerSiteRole || centerSite,
      status,
      startCreatedAt: startDate,
      endCreatedAt: endDate
        ? moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm")
        : undefined,
      count: isCount,
      // Stock users (e.g. Zipline delivery accounts) should see both products
      // currently at their blood bank (stock) and products they are currently
      // delivering (sourceStock). Use stockOrSourceStock for OR matching.
      // A caller that names its own scope (the dashboard cards) counts either
      // stock or sourceStock, never both, so the OR fallback must not widen the
      // list past the number that was clicked.
      stockOrSourceStock:
        noStockFilter || exactStock || sourceStock || awaitingReceptionFor
          ? undefined
          : stock || undefined,
      stock: exactStock,
      isLabeled,
      code,
      receivedBy,
      reason,
      component,
      bloodGroup,
      productIds,
      productType,
      q,
      // A caller that cares about expiry alone passes these directly, so the
      // bound lands on expiredDate verbatim. Deriving it from startDate sends
      // it through the shared filter helper first, which floors a start bound
      // to midnight - enough to let a unit that expired earlier today read as
      // still usable on a tab whose whole purpose is the window ahead of now.
      startExpiredDate: startExpiredDate || startDate,
      endExpiredDate:
        endExpiredDate ||
        (endDate
          ? moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm")
          : undefined),
      ...countStatusParams,
      inventoryStatus,
      sourceStock,
      startReturnDate,
      endReturnDate,
      excludeIncomingReception,
      excludeRejectedPending,
      awaitingReceptionFor,
      onlyNotExpired,
      onlyExpired,
    };

    if (
      filters.donation &&
      filters.donation !== "" &&
      !allowStatusWithDonation
    ) {
      delete filters.startCreatedAt;
      delete filters.endCreatedAt;
      delete filters.startExpiredDate;
      delete filters.endExpiredDate;
      delete filters.status;
    }

    if (filters.startExpiredDate || filters.endExpiredDate) {
      delete filters.startCreatedAt;
      delete filters.endCreatedAt;
    }

    const options = {
      method: "GET",
      url: `${API_URL}/inventory`,
      params: filters,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let { data, count, totalRecords } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    if (isCount) {
      return queryRes.data;
    }

    return { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};

export const getInventoryDashboardCounts = async (
  lng = "english",
  {
    donation,
    donationNumber,
    id,
    status,
    startDate,
    endDate,
    stock,
    sourceStock,
    bloodGroup,
    productIds,
    productType,
  } = {},
) => {
  try {
    const user = await getStorage();
    const roleScope = await defineRoleCenterSite();
    const roleStock = roleScope?.stock;

    const filters = {
      donation,
      donationNumber,
      id,
      status,
      bloodGroup,
      productIds,
      productType,
      stock: stock || roleStock,
      sourceStock,
      startCreatedAt: startDate,
      endCreatedAt: endDate
        ? moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm")
        : undefined,
      startExpiredDate: startDate,
      endExpiredDate: endDate
        ? moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm")
        : undefined,
    };

    const options = {
      method: "GET",
      url: `${API_URL}/inventory/dashboard-counts`,
      params: filters,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const { data } = await api(options);
    return data || {};
  } catch (error) {
    toastMessage("error", error);
  }
};

export const fetchInventoryByStatus = async (
  lng = "english",
  {
    page,
    limit,
    donation,
    id,
    type,
    center,
    centerSite,
    status,
    startDate,
    endDate,
    receivedBy,
  },
) => {
  try {
    const user = await getStorage();

    const { centerRole, centerSiteRole } = await defineRoleCenterSite();

    const options = {
      method: "GET",
      url: `${API_URL}/bloodInventory/analytics-status`,
      params: {
        page: page,
        limit: limit,
        donation,
        id,
        center: centerRole || center,
        centerSite: centerSiteRole || centerSite,
        status,
        startDate,
        endDate: moment(endDate).endOf("day").format("YYYY-MM-DD HH:mm"),
        receivedBy,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
      },
    };

    const queryRes = await api(options);

    if (type === "dropdown") {
      return queryRes.data.map((el) => {
        return {
          ...el,
          label: `${el.category},  ${el.bloodGroup}, ${el.donation._id}, ${el.label}`,
          value: el._id,
        };
      });
    }

    let { data, count, totalRecords } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    return { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};

export const fetchInventoryPerBloodGroup = async (lng = "english", params) => {
  try {
    const user = await getStorage();

// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
    const { centerRole, centerSiteRole } = await defineRoleCenterSite();

    const options = {
      method: "GET",
      url: `${API_URL}/bloodInventory/analytics`,
      params: {
        ...params,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let { data, count, totalRecords } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    return { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};

export const fetchCurrentInventoriesPerProductType = async (lng, params) => {
  try {
    const user = await getStorage();

    const { stock } = await defineRoleCenterSite();

    // The national inventory role is exactly the absence of this filter:
    // without dropping it here the dashboard would still be answered for
    // one blood bank however the screen above it was set.
    const countryWide = await hasNationalInventory();
    const roleStock = countryWide ? undefined : stock;

    const options = {
      method: "GET",
      url: `${API_URL}/inventory-current-stock-status`,
      params: {
        ...params,
        stock: params.stock
          ? params.stock
          : roleStock
          ? roleStock
          : undefined,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let { data, count, totalRecords } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    return { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};

export const fetchAndUpdateCurrentInventoriesStockStatus = async (
  lng,
  params,
) => {
  try {
    const options = {
      method: "GET",
      url: `${API_URL}/inventory/get-current-inventory-per-product-type`,
      params,
    };

    const queryRes = await api(options);

    let { data, count, totalRecords } = queryRes.data;

    if (!data) {
      data = queryRes.data;
    }

    return { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};
